import { enqueueSnackbar } from "notistack";
import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Team } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import { useTeam } from "~/contexts/TeamProvider";
import { stripePromise } from "~/helpers/load_stripe";
import { scrollMainToTop } from "~/helpers/scrollMainToTop";
import { tryCatch } from "~/helpers/tryCatch";
import { routes } from "~/router";

export type OfferKey = NonNullable<Team["autopto_offer"]>;

export type OfferError = {
  message: string;
  // A declined card, or none: another card retries the purchase.
  canChangeCard: boolean;
};

// The backend's message for an offer bought or declined meanwhile
// (activemodel payments/offer not_due in alli-ai-api's en.yml): it comes
// without a validation code.
const NOT_DUE_MESSAGE = "This offer is no longer available.";

const GENERIC_MESSAGE = "Something went wrong. Please try again.";

type RequestError = {
  status: number | null;
  message: string | null;
};

// The swagger client throws the Response, its body unread.
const readError = async (error: unknown): Promise<RequestError> => {
  if (!(error instanceof Response)) return { status: null, message: null };

  const body: unknown = await error.json().catch(() => null);
  const messages = (
    body as { error?: { validation_messages?: string[] } } | null
  )?.error?.validation_messages;

  return {
    status: error.status,
    message: messages?.length ? messages.join(" ") : null,
  };
};

const purchaseOffer = async (key: OfferKey) => {
  const result = await tryCatch(apiClient.billing.offersPurchaseCreate(key));

  return result.error
    ? { offer: null, error: await readError(result.error) }
    : { offer: result.data.data["payments/offer"], error: null };
};

/**
 * Buys or declines the due offer. Buying charges the saved card: 3D Secure
 * goes to Stripe.js, then the purchase is repeated to sync ahead of the
 * webhook (a 403 then means the webhook has ended the offer: bought). An
 * offer bought or declined meanwhile (another tab) rereads the team, and
 * the page leaves once it's gone.
 */
export function useOfferPurchase(key: OfferKey) {
  const { refetchTeam } = useTeam();
  const navigate = useNavigate();
  const [action, setAction] = useState<"purchase" | "decline" | null>(null);
  const [error, setError] = useState<OfferError | null>(null);
  // Set before the first await: a double click mustn't start a second one.
  const isBusyRef = useRef(false);

  const leave = useCallback(
    (message: string, variant: "success" | "info") => {
      enqueueSnackbar(message, { variant });
      navigate(routes.home(), { replace: true });
      // The dashboard from its top, not the offer's scroll.
      scrollMainToTop();
      void refetchTeam();
    },
    [navigate, refetchTeam],
  );

  const settle = useCallback(
    async (status: string | null) => {
      switch (status) {
        // Null: paid without an intent.
        case null:
        case "succeeded":
          leave(
            "Thank you! Check your email for full order details",
            "success",
          );
          break;
        case "processing":
          leave(
            "Your payment is processing. We'll email you the order details once it goes through.",
            "info",
          );
          break;
        default:
          // Canceled: the webhook settles it.
          setError({
            message: "The payment didn't go through. Please try again.",
            canChangeCard: true,
          });
          await refetchTeam();
      }
    },
    [leave, refetchTeam],
  );

  const fail = useCallback(
    async ({ status, message }: RequestError) => {
      if (status === 403 || message === NOT_DUE_MESSAGE) {
        await refetchTeam();

        return;
      }

      // A declined card ("Your card was declined.") or none.
      if (status === 422 && message) {
        setError({ message, canChangeCard: true });

        return;
      }

      setError({ message: GENERIC_MESSAGE, canChangeCard: false });
    },
    [refetchTeam],
  );

  const run = useCallback(
    async (
      nextAction: "purchase" | "decline",
      perform: () => Promise<void>,
    ) => {
      if (isBusyRef.current) return;

      isBusyRef.current = true;
      setAction(nextAction);
      setError(null);

      try {
        await perform();
      } finally {
        isBusyRef.current = false;
        setAction(null);
      }
    },
    [],
  );

  const purchase = useCallback(
    () =>
      run("purchase", async () => {
        const first = await purchaseOffer(key);

        if (first.error) return fail(first.error);

        const { payment_status, client_secret } = first.offer;

        if (payment_status !== "requires_action" || !client_secret) {
          return settle(payment_status);
        }

        const stripe = await stripePromise;

        if (!stripe) {
          return setError({ message: GENERIC_MESSAGE, canChangeCard: false });
        }

        const { error: actionError } = await stripe.handleNextAction({
          clientSecret: client_secret,
        });

        // Cancelled or failed: a retry starts 3D Secure again. Stripe's own
        // message comes in the browser's language.
        if (actionError) {
          return setError({
            message:
              "We couldn't verify your card. Please try again or use another card.",
            canChangeCard: true,
          });
        }

        const synced = await purchaseOffer(key);

        if (synced.error?.status === 403) return settle("succeeded");
        if (synced.error) return fail(synced.error);

        return settle(synced.offer.payment_status);
      }),
    [run, key, fail, settle],
  );

  const decline = useCallback(
    () =>
      run("decline", async () => {
        const result = await tryCatch(
          apiClient.billing.offersDeclineCreate(key),
        );

        if (result.error) {
          const requestError = await readError(result.error);

          // Bought meanwhile: the team tells.
          if (requestError.status === 422 || requestError.status === 403) {
            await refetchTeam();

            return;
          }

          return setError({ message: GENERIC_MESSAGE, canChangeCard: false });
        }

        // The downsell after the upsell, or none: the page follows the team.
        await refetchTeam();

        // The last one: the dashboard from its top.
        if (key === "autopto_downsell") scrollMainToTop();
      }),
    [run, key, refetchTeam],
  );

  return { action, error, purchase, decline };
}
